import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { salesHubApi } from '@/services/api';
import { X, AlertTriangle, Loader2, Trash2, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { getApiErrorMessage } from '@/utils/apiError';
import toast from 'react-hot-toast';

interface Props {
  invoiceIds: string[];
  onClose: () => void;
  onSuccess?: () => void;
}

export default function BulkDeleteSalesModal({ invoiceIds, onClose, onSuccess }: Props) {
  useEscapeToClose(onClose);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const queryClient = useQueryClient();
  const count = invoiceIds.length;

  const deleteMutation = useMutation({
    mutationFn: async (pwd: string) => {
      const results: { id: string; ok: boolean; error?: string }[] = [];
      for (const id of invoiceIds) {
        try {
          await salesHubApi.deleteSaleInvoice(id, pwd);
          results.push({ id, ok: true });
        } catch (err: any) {
          results.push({
            id,
            ok: false,
            error: getApiErrorMessage(err, 'Delete failed'),
          });
          // Wrong password — stop immediately
          const msg = String(results[results.length - 1].error || '');
          if (/incorrect password|password/i.test(msg) && results.filter((r) => r.ok).length === 0) {
            throw err;
          }
        }
      }
      return results;
    },
    onSuccess: (results) => {
      const ok = results.filter((r) => r.ok).length;
      const fail = results.filter((r) => !r.ok);
      queryClient.invalidateQueries({ queryKey: ['salesHubList'] });
      queryClient.invalidateQueries({ queryKey: ['salesHubCounts'] });
      queryClient.invalidateQueries({ queryKey: ['salesHubDetail'] });
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['stock'] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      if (ok > 0) {
        toast.success(
          fail.length
            ? `Deleted ${ok} of ${count} sale(s). ${fail.length} failed.`
            : `Deleted ${ok} selected sale(s) and reverted stock.`,
        );
      }
      if (fail.length && ok === 0) {
        setError(fail[0].error || 'Failed to delete selected sales.');
        return;
      }
      if (onSuccess) onSuccess();
      onClose();
    },
    onError: (err: any) => {
      setError(getApiErrorMessage(err, 'Failed to delete selected sales. Check your password.'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError('Admin password is required.');
      return;
    }
    if (!count) {
      setError('No sales selected.');
      return;
    }
    setError('');
    deleteMutation.mutate(password);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200">
        <div className="flex items-center justify-between p-5 border-b border-rose-100 bg-rose-50/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center text-rose-600 shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-base">Reset Selected Sales</h3>
              <p className="text-xs text-rose-700 mt-0.5">
                {count} sale{count === 1 ? '' : 's'} only — does not wipe all sales or returns
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            type="button"
            className="p-1.5 hover:bg-rose-100 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 max-h-36 overflow-y-auto">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
              Will delete
            </div>
            <ul className="space-y-1 font-mono text-xs text-slate-800">
              {invoiceIds.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          </div>

          <div className="bg-amber-50/80 border border-amber-200/80 rounded-xl p-3.5 space-y-2 text-xs text-amber-900">
            <div className="font-semibold flex items-center gap-1.5 text-amber-800">
              <RotateCcw className="w-4 h-4 text-amber-600 shrink-0" />
              <span>For each selected sale:</span>
            </div>
            <ul className="list-disc list-inside space-y-1 text-amber-800/90 pl-1">
              <li>Stock is restored for that invoice.</li>
              <li>Payments / cash ledger lines for that sale are removed.</li>
              <li>Only returns linked to these invoices are removed (other returns stay).</li>
            </ul>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Enter Admin Password to confirm:
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                autoFocus
                placeholder="Admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-xl px-3.5 py-2.5 pr-10 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700">{error}</div>
          )}

          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={deleteMutation.isPending}
              className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={deleteMutation.isPending || !password || !count}
              className="px-4 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 rounded-xl flex items-center gap-2"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              <span>
                {deleteMutation.isPending
                  ? `Resetting ${count}…`
                  : `Reset ${count} selected`}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
